import { refreshSession } from './client';
import { sessionStore, type Session, type SessionStore } from './session';

/** Se renueva este tiempo antes de que venza el access token (15 min en el API). */
export const RENEW_AHEAD_MS = 60_000;
/** Entre dos renovaciones seguidas pasa al menos esto: un token de vida muy corta no puede girar en bucle. */
export const MIN_GAP_MS = 10_000;
/** `setTimeout` guarda el plazo en 32 bits con signo; más allá se dispara de inmediato. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/**
 * Una pestaña abierta no pide nada al API mientras nadie toca la pantalla (las consultas no se
 * refrescan al enfocar), así que no se enteraba de que la sesión caducó. Mientras haya una sesión con
 * token, se renueva poco antes de que venza y al volver a la pestaña o a la red. Si el API rechaza la
 * sesión, `refresh` llama a `store.expire()` y la app abre el ingreso. Con la renovación hecha, el
 * token nuevo reprograma la siguiente. Visitantes y sesión sin conexión (sin token) no hacen nada.
 */
export function installSessionWatch(
  store: SessionStore = sessionStore,
  refresh: () => Promise<Session | null> = refreshSession,
  now: () => number = Date.now,
): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let renewing = false;
  let lastRenewAt = Number.NEGATIVE_INFINITY;

  const check = (): void => {
    clearTimeout(timer);
    const session = store.get();
    if (!session?.accessToken || renewing) return;

    const untilRenew = session.expiresAt - RENEW_AHEAD_MS - now();
    const untilAllowed = lastRenewAt + MIN_GAP_MS - now();
    const wait = Math.max(untilRenew, untilAllowed);
    if (wait > 0) {
      timer = setTimeout(check, Math.min(wait, MAX_TIMEOUT_MS));
      return;
    }

    renewing = true;
    lastRenewAt = now();
    refresh()
      .catch(() => null)
      .finally(() => {
        renewing = false;
        check();
      });
  };

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') check();
  };

  const unsubscribe = store.subscribe(check);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', check);
  window.addEventListener('online', check);
  check();

  return () => {
    clearTimeout(timer);
    unsubscribe();
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', check);
    window.removeEventListener('online', check);
  };
}
